import { useCallback, useEffect, useRef, useState } from "react";

import { uploadVideoChunk } from "@/api/tests-api";

/**
 * One continuous camera recording for a whole screening round.
 *
 * NOT A CLIP PER QUESTION. The sitting is one unbroken take and question
 * boundaries are offsets into it, which is what makes the file navigable
 * for the assessor and what makes the round hard to game: there is no
 * cut between answers to hide anything in.
 *
 * FRAGMENTS GO UP WHILE IT RECORDS. `MediaRecorder.start(timeslice)`
 * hands over a piece every few seconds and each is POSTed as it arrives.
 * The round is one attempt with no restart, so a blob delivered at
 * Submit would lose the entire sitting to a crash at minute fourteen;
 * this way a crash costs the seconds since the last fragment.
 *
 * A FAILED UPLOAD IS RETRIED, NOT DROPPED, and the recorder does not
 * stop for it. A candidate whose wifi flickers must keep answering: the
 * queue simply grows and drains when the link returns. Dropping a
 * fragment would splice the file at a point no decoder expects, because
 * fragments are continuation clusters of one stream rather than
 * standalone files.
 *
 * SEGMENTS. If the recorder has to be torn down and restarted -- the
 * stream ended, the device was lost -- the new one writes its own WebM
 * header, so it cannot be byte-joined to what came before. It becomes a
 * new SEGMENT, and the server concatenates segments with ffmpeg.
 */

/** How often MediaRecorder hands over a fragment. */
const TIMESLICE_MS = 4000;

/**
 * Bitrate, set explicitly rather than left to the browser.
 *
 * A talking head at 640x480 needs very little, and the default is
 * several times this. It is the single lever on what a sitting costs:
 * at roughly 5 MB a minute a fifteen-minute round is ~75 MB per
 * candidate, against about 175 MB for every case image in the library.
 */
const VIDEO_BITS = 600_000;
const AUDIO_BITS = 64_000;

const CONSTRAINTS: MediaStreamConstraints = {
  video: { width: { ideal: 640 }, height: { ideal: 480 }, frameRate: { ideal: 24 } },
  audio: { echoCancellation: true, noiseSuppression: true },
};

export type RecorderPhase = "idle" | "ready" | "recording" | "error";

export interface ScreeningRecorder {
  phase: RecorderPhase;
  /** Why the camera could not be opened, in words a candidate can act on. */
  error: string | null;
  stream: MediaStream | null;
  /** Seconds of recording so far — the offset a chapter is stamped with. */
  elapsed: number;
  /** 0..1, from the live stream. Evidence the microphone is actually on. */
  level: number;
  /** Fragments accepted by the server. */
  uploaded: number;
  /** Fragments waiting because an upload failed. */
  pending: number;
  enable: () => Promise<void>;
  start: () => void;
  stop: () => Promise<void>;
  release: () => void;
}

function describe(err: unknown): string {
  const name = (err as { name?: string })?.name ?? "";
  if (name === "NotAllowedError" || name === "SecurityError") {
    return "Camera and microphone access was refused. Allow it in your browser, then try again.";
  }
  if (name === "NotFoundError" || name === "OverconstrainedError") {
    return "No camera or microphone was found. Connect one and try again.";
  }
  if (name === "NotReadableError") {
    return "Your camera is in use by another application. Close it and try again.";
  }
  return "The camera could not be started. Check your browser settings and try again.";
}

export function useScreeningRecorder(attemptId: string): ScreeningRecorder {
  const [phase, setPhase] = useState<RecorderPhase>("idle");
  const [error, setError] = useState<string | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [level, setLevel] = useState(0);
  const [uploaded, setUploaded] = useState(0);
  const [pending, setPending] = useState(0);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const rafRef = useRef<number | null>(null);
  const startedAtRef = useRef<number | null>(null);
  const segmentRef = useRef(0);
  const sequenceRef = useRef(0);
  // Fragments that have not reached the server yet. IN ORDER, always:
  // they are continuation clusters, so one arriving out of sequence
  // would corrupt the stream rather than merely delay it.
  const queueRef = useRef<{ segment: number; sequence: number; blob: Blob }[]>([]);
  const drainingRef = useRef(false);

  const drain = useCallback(async () => {
    if (drainingRef.current) return;
    drainingRef.current = true;
    try {
      while (queueRef.current.length > 0) {
        const next = queueRef.current[0]!;
        try {
          await uploadVideoChunk(attemptId, next.segment, next.sequence, next.blob);
        } catch {
          // Leave it at the head and stop. The next fragment's arrival
          // tries again, so a flaky link recovers on its own without the
          // candidate being told to do anything -- they are mid-answer.
          break;
        }
        queueRef.current.shift();
        setUploaded((n) => n + 1);
        setPending(queueRef.current.length);
      }
    } finally {
      drainingRef.current = false;
      setPending(queueRef.current.length);
    }
  }, [attemptId]);

  const watchLevel = useCallback((source: MediaStream) => {
    // THE SAME STREAM THE RECORDER MUXES, never a second capture. A
    // meter fed from anywhere else would be reassurance rather than
    // evidence that the microphone is live -- which is the one thing a
    // candidate cannot otherwise check.
    const Ctx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext })
        .webkitAudioContext;
    const ctx = new Ctx();
    audioCtxRef.current = ctx;
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 512;
    // NEVER connected to the destination: routing the microphone to the
    // speakers gives a feedback howl to anyone not wearing headphones.
    ctx.createMediaStreamSource(source).connect(analyser);

    const data = new Uint8Array(analyser.frequencyBinCount);
    const tick = () => {
      analyser.getByteTimeDomainData(data);
      let peak = 0;
      for (const v of data) peak = Math.max(peak, Math.abs(v - 128));
      setLevel(Math.min(1, peak / 96));
      rafRef.current = requestAnimationFrame(tick);
    };
    tick();
  }, []);

  const enable = useCallback(async () => {
    if (streamRef.current) return;
    try {
      const media = await navigator.mediaDevices.getUserMedia(CONSTRAINTS);
      streamRef.current = media;
      setStream(media);
      watchLevel(media);
      setPhase("ready");
      setError(null);
    } catch (err) {
      setPhase("error");
      setError(describe(err));
    }
  }, [watchLevel]);

  const start = useCallback(() => {
    const media = streamRef.current;
    if (!media || recorderRef.current) return;

    const rec = new MediaRecorder(media, {
      videoBitsPerSecond: VIDEO_BITS,
      audioBitsPerSecond: AUDIO_BITS,
    });
    rec.ondataavailable = (event) => {
      if (!event.data || event.data.size === 0) return;
      queueRef.current.push({
        segment: segmentRef.current,
        sequence: sequenceRef.current,
        blob: event.data,
      });
      sequenceRef.current += 1;
      setPending(queueRef.current.length);
      void drain();
    };
    rec.onerror = () => {
      // The stream died mid-sitting. Everything already uploaded stands;
      // whatever comes next starts a new SEGMENT, because a restarted
      // recorder writes its own header and cannot be byte-joined.
      segmentRef.current += 1;
      sequenceRef.current = 0;
      recorderRef.current = null;
    };
    recorderRef.current = rec;
    startedAtRef.current = performance.now();
    rec.start(TIMESLICE_MS);
    setPhase("recording");
  }, [drain]);

  const stop = useCallback(async () => {
    const rec = recorderRef.current;
    if (!rec) return;
    await new Promise<void>((resolve) => {
      rec.onstop = () => resolve();
      try {
        rec.stop();
      } catch {
        resolve();
      }
    });
    recorderRef.current = null;
    setPhase(streamRef.current ? "ready" : "idle");
    await drain();
  }, [drain]);

  const release = useCallback(() => {
    // The OS recording indicator stays lit until every track is stopped,
    // and an AudioContext left open keeps the tab awake.
    recorderRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setStream(null);
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    void audioCtxRef.current?.close().catch(() => undefined);
    audioCtxRef.current = null;
    setPhase("idle");
    setLevel(0);
  }, []);

  // Elapsed recording time, on performance.now(): monotonic, so changing
  // the system clock cannot move an offset.
  useEffect(() => {
    if (phase !== "recording") return undefined;
    const id = window.setInterval(() => {
      const from = startedAtRef.current;
      if (from != null) setElapsed(Math.floor((performance.now() - from) / 1000));
    }, 250);
    return () => window.clearInterval(id);
  }, [phase]);

  // Keep trying to hand over anything stuck, even while nothing new is
  // being produced -- a link that returns between fragments should not
  // wait four seconds to find out.
  useEffect(() => {
    const id = window.setInterval(() => {
      if (queueRef.current.length > 0) void drain();
    }, 3000);
    return () => window.clearInterval(id);
  }, [drain]);

  useEffect(() => release, [release]);

  return {
    phase,
    error,
    stream,
    elapsed,
    level,
    uploaded,
    pending,
    enable,
    start,
    stop,
    release,
  };
}
